import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper, { PopperPlacementType } from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Button from "components-redesign/Button/Button";
import { FIGMA_COLORS } from "components-redesign/styles/colors";
import TYPOGRAPHY from "components-redesign/styles/typography";
import {
  TypographyBodySmall,
  TypographyTitleLarge,
} from "components-redesign/Typography/Typography";
import { makeStyles } from "components/mui-core";
import { convertDateTimeToSameISO, convertISOtoSameDateTime } from "helpers";
import { ReactComponent as ClockIconSvg } from "icons/redesign/datePicker/clock.svg";
import { ISOstring } from "model/modelTypes";
import {
  ChangeEvent,
  Dispatch,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const useStyles = makeStyles()({
  timePopoverContent: {
    padding: 24,
    borderRadius: "16px",
    backgroundColor: "#fff",
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    minWidth: 280,
  },
  timeBlock: {
    display: "inline-block",
  },
  timeBlockCentered: {
    width: "100%",
  },
  timeInputContainer: {
    display: "flex",
    alignItems: "flex-start",
  },
  inputsCentered: {
    justifyContent: "center",
  },
  timeInputField: {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      backgroundColor: FIGMA_COLORS.surfaceContainerHighestLight,
      border: "2px solid transparent",
      padding: "9px 16px",
      boxSizing: "border-box",
    },
    "& .MuiInputBase-input": {
      ...TYPOGRAPHY.titleLarge,
      width: "32px",
      height: "28px",
      textAlign: "center",
      color: FIGMA_COLORS.onSurfaceLight,
      padding: 0,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiInputBase-root": {
      borderColor: FIGMA_COLORS.outlineLight,
    },
    "& .Mui-focused": {
      borderColor: `${FIGMA_COLORS.primaryLight} !important`,
      backgroundColor: FIGMA_COLORS.primaryContainerLight,
    },
    "& .Mui-focused .MuiInputBase-input": {
      color: FIGMA_COLORS.onPrimaryContainerLight,
    },
  },
  timeSeparator: {
    width: "24px",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: FIGMA_COLORS.onSurfaceLight,
  },
  actionButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  cancelButton: {
    color: FIGMA_COLORS.primaryLight,
    textTransform: "none",
    fontWeight: 500,
  },
  okButton: {
    color: FIGMA_COLORS.primaryLight,
    textTransform: "none",
    fontWeight: 500,
  },
  clockIcon: {
    color: FIGMA_COLORS.surfaceVariantDark,
  },
  warningHourField: {
    "& .MuiInputBase-root": {
      border: `2px solid ${FIGMA_COLORS.errorLight}`,
      backgroundColor: FIGMA_COLORS.errorContainerLight,
    },
  },
});

interface Props {
  open?: boolean;
  anchorEl?: Element | null;
  onClose?: () => void;
  selectedDate: ISOstring;
  onTimeSelect?: (dateWithTime: ISOstring) => void;
  onCancel?: () => void;
  onChange?: (hour: string, minute: string) => void;
  inline?: boolean;
  placement?: PopperPlacementType;
  label?: string;
  disablePastTime?: boolean;
  centered?: boolean;
}

const TimePicker: FC<Props> = ({
  open = false,
  anchorEl,
  onClose,
  selectedDate,
  onTimeSelect,
  onCancel,
  onChange,
  inline = false,
  placement,
  label,
  disablePastTime = false,
  centered = false,
}) => {
  const { classes, cx } = useStyles();
  const date = useMemo(
    () => (selectedDate ? convertISOtoSameDateTime(selectedDate) : null),
    [selectedDate]
  );
  const [hour, setHour] = useState<string>(
    date ? String(date.getHours()).padStart(2, "0") : "00"
  );
  const [minute, setMinute] = useState<string>(
    date ? String(date.getMinutes()).padStart(2, "0") : "00"
  );
  const isToday = useMemo(() => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, [date]);

  const currentHour = useMemo(() => {
    return new Date().getHours();
  }, []);

  const isCurrentHourPast = useMemo(() => {
    const currentHourValue = parseInt(hour) || 0;
    return disablePastTime && isToday && currentHourValue < currentHour;
  }, [disablePastTime, isToday, hour, currentHour]);

  useEffect(() => {
    if (date) {
      setHour(String(date.getHours()).padStart(2, "0"));
      setMinute(String(date.getMinutes()).padStart(2, "0"));
    } else {
      setHour("00");
      setMinute("00");
    }
  }, [date]);

  const handleTimeInputChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement>,
      setter: Dispatch<React.SetStateAction<string>>,
      maxValue: number
    ) => {
      const value = event.target.value;
      if (value === "") {
        setter("");
        return;
      }
      if (/^\d*$/.test(value) && value.length <= 2) {
        const numValue = Number(value);
        if (value.length === 2 && numValue > maxValue) {
          setter(String(maxValue).padStart(2, "0"));
        } else if (value.length === 1 && numValue > maxValue / 10) {
          setter(value);
        } else {
          setter(value);
        }
      }
    },
    []
  );

  const formatTimeOnBlur = useCallback(
    (
      value: string,
      setter: React.Dispatch<React.SetStateAction<string>>,
      maxValue: number
    ) => {
      if (value === "") {
        setter("00");
        return;
      }
      let numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        numValue = 0;
      }
      if (numValue < 0) numValue = 0;
      if (numValue > maxValue) numValue = maxValue;
      setter(String(numValue).padStart(2, "0"));
    },
    []
  );

  const handleHourChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "") {
        setHour("");
        return;
      }

      if (/^\d*$/.test(value) && value.length <= 2) {
        const numValue = Number(value);

        if (value.length === 2 && numValue > 23) {
          setHour("23");
        } else {
          setHour(value);
        }
      }

      if (onChange) {
        onChange(value, minute);
      }
    },
    [onChange, minute]
  );

  const handleMinuteChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleTimeInputChange(event, setMinute, 59);
      if (onChange) {
        onChange(hour, event.target.value);
      }
    },
    [handleTimeInputChange, onChange, hour]
  );

  const handleHourBlur = useCallback(() => {
    if (hour === "") {
      setHour("00");
      return;
    }

    let numValue = parseInt(hour, 10);
    if (isNaN(numValue)) {
      numValue = 0;
    }
    if (numValue < 0) numValue = 0;
    if (numValue > 23) numValue = 23;

    if (disablePastTime && isToday && numValue < currentHour) {
      numValue = currentHour;
    }

    setHour(String(numValue).padStart(2, "0"));

    if (onChange) {
      onChange(String(numValue).padStart(2, "0"), minute);
    }
  }, [hour, minute, onChange, disablePastTime, isToday, currentHour]);

  const handleMinuteBlur = useCallback(() => {
    formatTimeOnBlur(minute, setMinute, 59);
    if (onChange) {
      onChange(hour, minute);
    }
  }, [formatTimeOnBlur, hour, minute, onChange]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    onClose?.();
  }, [onCancel, onClose]);

  const handleOk = useCallback(() => {
    if (date) {
      let newHour = hour === "" ? 0 : parseInt(hour, 10);
      const newMinute = minute === "" ? 0 : parseInt(minute, 10);

      if (disablePastTime && isToday && newHour < currentHour) {
        newHour = currentHour;
      }

      const dateWithTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        newHour,
        newMinute
      );
      onTimeSelect?.(convertDateTimeToSameISO(dateWithTime));
      onClose?.();
    }
  }, [
    date,
    hour,
    minute,
    onTimeSelect,
    onClose,
    disablePastTime,
    isToday,
    currentHour,
  ]);

  const handleClickAway = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const timePickerContent = (
    <div className={centered ? classes.timeBlockCentered : classes.timeBlock}>
      {label && (
        <TypographyBodySmall
          sx={{ color: FIGMA_COLORS.onSurfaceVariantLight, mb: "12px" }}>
          {label}
        </TypographyBodySmall>
      )}
      <Box
        className={cx(
          classes.timeInputContainer,
          centered && classes.inputsCentered
        )}>
        <Box>
          <TextField
            value={hour}
            onChange={handleHourChange}
            onBlur={handleHourBlur}
            className={cx(
              classes.timeInputField,
              isCurrentHourPast && classes.warningHourField
            )}
          />
          <TypographyBodySmall
            sx={{ color: FIGMA_COLORS.onSurfaceVariantLight, mt: "4px" }}>
            Hour
          </TypographyBodySmall>
        </Box>
        <TypographyTitleLarge className={classes.timeSeparator}>
          :
        </TypographyTitleLarge>
        <Box>
          <TextField
            value={minute}
            onChange={handleMinuteChange}
            onBlur={handleMinuteBlur}
            className={classes.timeInputField}
          />
          <TypographyBodySmall
            sx={{ color: FIGMA_COLORS.onSurfaceVariantLight, mt: "4px" }}>
            Minute
          </TypographyBodySmall>
        </Box>
      </Box>
    </div>
  );

  if (inline) {
    return timePickerContent;
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement || "bottom-start"}
      sx={{
        zIndex: 10000,
        marginInline: "10px !important",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
      }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div className={classes.timePopoverContent}>
          {timePickerContent}
          <Box className={classes.actionButtons}>
            <ClockIconSvg className={classes.clockIcon} />
            <Box>
              <Button
                variant="text"
                onClick={handleCancel}
                className={classes.cancelButton}>
                Cancel
              </Button>
              <Button
                variant="text"
                onClick={handleOk}
                className={classes.okButton}>
                OK
              </Button>
            </Box>
          </Box>
        </div>
      </ClickAwayListener>
    </Popper>
  );
};

export default TimePicker;
